if (!customElements.get('featured-collection-tabs')) {
  /**
   * Tracks the product URL set when opening the plan-filter modal from a product card.
   * Cleared after Apply redirect or modal close.
   * @type {string | null}
   */
  let _pfPendingProductUrl = null;

  customElements.define(
    'featured-collection-tabs',
    class FeaturedCollectionTabs extends HTMLDivElement {
      constructor() {
        super();
        this.handlePlanDetailsClick = this.handlePlanDetailsClick.bind(this);
        this.handleGetPlanClick = this.handleGetPlanClick.bind(this);
      }

      connectedCallback() {
        this.tabPanels = this.querySelector('.featured-collection-tabs__panels');
        this.tabBtns = this.querySelectorAll('.featured-collection-tabs__tab');
        /** @type {number} */
        this.currentIndex = 0;
        if (this.tabPanels && this.tabBtns.length) {
          this.init();
        }
        this.initPlanCards();
        this._initPlanFilterInterceptor();
      }

      init() {
        if (this.tabBtns) {
          this.tabBtns.forEach((btn) => {
            btn.addEventListener('click', this.onClick.bind(this));
          });
        }

        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          this.addEventListener('shopify:block:select', (e) => {
            const target = e.target;
            if (target && target instanceof HTMLElement && target.dataset) {
              const index = Number(target.dataset.index);
              if (!isNaN(index)) {
                this.setActiveTab(index);
              }
            }
          });
        }
      }

      initPlanCards() {
        // Handle Plan Details buttons
        const planDetailsBtns = this.querySelectorAll('.product-card__plan-details-btn');
        planDetailsBtns.forEach((btn) => {
          btn.addEventListener('click', this.handlePlanDetailsClick);
        });

        // Handle "Select Plan" buttons — open plan-filter calendar modal
        this.querySelectorAll('[data-open-plan-filter]').forEach((btn) => {
          if (btn._pfOpenInitialized) return;
          btn._pfOpenInitialized = true;
          btn.addEventListener('click', this.handleGetPlanClick);
        });
      }

      /**
       * Sets up a second listener on the plan-filter Apply button (runs after plan-filter.js handler).
       * If a product card URL is pending, reads synced hidden inputs and redirects.
       * Also clears pending URL on modal close.
       */
      _initPlanFilterInterceptor() {
        if (this._pfInterceptorReady) return;
        this._pfInterceptorReady = true;

        // Apply — runs AFTER plan-filter.js onApplyClick which calls syncHiddenInputs()
        const applyBtn = document.querySelector('[data-plan-filter-apply]');
        if (applyBtn instanceof HTMLButtonElement) {
          applyBtn.addEventListener('click', () => {
            if (!_pfPendingProductUrl) return;

            // plan-filter.js already called syncHiddenInputs() at this point
            const startInput = document.querySelector('[data-plan-filter-start-input]');
            const endInput = document.querySelector('[data-plan-filter-end-input]');
            const modeInput = document.querySelector('[data-plan-filter-mode-input]');

            const start = startInput instanceof HTMLInputElement ? startInput.value : '';
            const end = endInput instanceof HTMLInputElement ? endInput.value : '';
            const mode = modeInput instanceof HTMLInputElement ? modeInput.value : '';

            // Guard: plan-filter.js returned early (canApply was false), inputs are empty
            if (!start) return;

            const targetUrl = new URL(_pfPendingProductUrl, window.location.origin);
            targetUrl.searchParams.set('plan_filter_start', start);
            if (end) targetUrl.searchParams.set('plan_filter_end', end);
            if (mode) targetUrl.searchParams.set('plan_filter_mode', mode);

            const usageInput = document.querySelector('[data-plan-filter-usage-input]');
            const usage = usageInput instanceof HTMLInputElement ? usageInput.value : '';
            if (usage) targetUrl.searchParams.set('plan_filter_usage', usage);

            _pfPendingProductUrl = null;
            window.location.href = targetUrl.toString();
          });
        }

        // Clear pending URL on any close button click
        document.querySelectorAll('[data-plan-filter-close]').forEach((btn) => {
          btn.addEventListener('click', () => {
            _pfPendingProductUrl = null;
          });
        });
      }

      /**
       * @param {Event} e
       */
      handlePlanDetailsClick(e) {
        if (!(e.target instanceof HTMLElement)) return;
        const button = e.target;
        const productId = button.dataset.productId;
        if (!productId) return;

        const detailsBlock = this.querySelector(`#plan-details-${productId}`);
        if (!detailsBlock) return;

        const isHidden = detailsBlock.hasAttribute('hidden');
        if (isHidden) {
          detailsBlock.removeAttribute('hidden');
          button.setAttribute('aria-expanded', 'true');
          button.textContent = 'Hide Details';
        } else {
          detailsBlock.setAttribute('hidden', '');
          button.setAttribute('aria-expanded', 'false');
          button.textContent = 'Plan Details';
        }
      }

      /**
       * Fires on "Select Plan" click.
       * Stores target product URL and opens the plan-filter calendar modal.
       * @param {Event} e
       */
      handleGetPlanClick(e) {
        e.preventDefault();
        const button = /** @type {HTMLElement} */ (e.currentTarget);
        const productUrl = button.dataset.productUrl;
        if (!productUrl) return;

        // Store the product URL — picked up by the Apply interceptor after dates are confirmed
        _pfPendingProductUrl = productUrl;

        // Open the plan-filter calendar modal via its own open button
        const openBtn = /** @type {HTMLButtonElement | null} */ (
          document.querySelector('[data-plan-filter-open]')
        );
        if (openBtn instanceof HTMLButtonElement) {
          openBtn.click();
        }
      }

      /**
       * @param {Event} e
       */
      onClick(e) {
        e.preventDefault();
        if (!(e.target instanceof HTMLElement)) return;
        const button = e.target.closest('button');
        if (!(button instanceof HTMLElement) || !button.dataset) return;
        const index = Number(button.dataset.index);
        if (!isNaN(index)) {
          this.setActiveTab(index);
        }
      }

      /**
       * @param {number} index
       */
      setActiveTab(index) {
        if (this.currentIndex === index) return;
        if (!this.tabPanels || !this.tabBtns) return;

        let newTab, newBtn, currentItem;

        const currentIdx = this.currentIndex;
        if (typeof currentIdx === 'number' && currentIdx < this.tabBtns.length) {
          currentItem = this.tabBtns[currentIdx];
        }
        if (typeof index === 'number' && index < this.tabBtns.length) {
          newBtn = this.tabBtns[index];
        }

        if (currentItem) {
          currentItem.classList.remove('active');
          currentItem.setAttribute('aria-selected', 'false');
        }
        if (newBtn) {
          newBtn.classList.add('active');
          newBtn.setAttribute('aria-selected', 'true');
        }

        const tabPanel = this.tabPanels.querySelectorAll('.featured-collection-tabs__panel');
        tabPanel.forEach((panel) => {
          panel.setAttribute('hidden', '');
          panel.classList.remove('active');
        });

        newTab = this.tabPanels.querySelector(`.featured-collection-tabs__panel[data-index="${index}"]`);

        if (!newTab) {
          const template = this.tabPanels.querySelector(`template[data-index="${index}"]`);
          if (template && template instanceof HTMLTemplateElement && template.content) {
            const clonedContent = template.content.cloneNode(true);
            if (clonedContent instanceof DocumentFragment && clonedContent.firstElementChild instanceof HTMLElement) {
              newTab = clonedContent.firstElementChild;
              this.tabPanels.appendChild(newTab);
            }
          }
        }

        if (newTab) {
          newTab.removeAttribute('hidden');
          newTab.classList.add('active');
          // Re-initialize plan cards for newly loaded content
          this.initPlanCards();
        }

        this.currentIndex = index;

        this.dispatchEvent(
          new CustomEvent('tabChange', {
            bubbles: true,
            detail: { selectedIndex: index, selectedTab: newTab },
          })
        );
      }
    },
    { extends: 'div' }
  );
}
